// Phase 2 §2: Mode Toggle Component
// Legal / Scenario mode switcher with visual indicators

import { Shield, Sparkles } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useProjectionMode } from '@/hooks/useProjectionMode';
import { cn } from '@/lib/utils';

interface CapTableModeToggleProps {
  className?: string;
  showLabels?: boolean;
}

export function CapTableModeToggle({ className, showLabels = true }: CapTableModeToggleProps) {
  const { mode, toggleMode, isScenarioMode } = useProjectionMode();

  return (
    <div className={cn('flex items-center gap-3', className)}>
      {showLabels && (
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-success" />
          <span
            className={cn(
              'text-sm font-medium transition-colors',
              !isScenarioMode ? 'text-success' : 'text-muted-foreground'
            )}
          >
            Legal
          </span>
        </div>
      )}

      <Switch
        checked={isScenarioMode}
        onCheckedChange={toggleMode}
        className={cn('data-[state=checked]:bg-warning', 'data-[state=unchecked]:bg-success')}
        aria-label="Toggle between Legal and Scenario mode"
      />

      {showLabels && (
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-warning" />
          <span
            className={cn(
              'text-sm font-medium transition-colors',
              isScenarioMode ? 'text-warning' : 'text-muted-foreground'
            )}
          >
            Scenario
          </span>
        </div>
      )}

      {!showLabels && (
        <Badge
          variant={isScenarioMode ? 'default' : 'secondary'}
          className={cn(
            'ml-2',
            isScenarioMode
              ? 'bg-warning text-warning-foreground'
              : 'bg-success text-success-foreground'
          )}
        >
          {mode === 'LEGAL' ? '✓ Certified' : '⚠ Projection'}
        </Badge>
      )}
    </div>
  );
}
